import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Settings, Volume2, VolumeX, Globe, Heart, Coins } from "lucide-react";
import { useGame } from "@/context/GameContext";
import { t } from "@/lib/translations";
import { LAYERS, ageList } from "@/lib/layers";
import { playSfx } from "@/lib/sounds";
import ParentGate from "@/components/ParentGate";
import OnboardingWizard from "@/components/OnboardingWizard";

export default function LandingPage() {
  const navigate = useNavigate();
  const { language, soundOn, toggleLanguage, toggleSound, parentId, loaded } = useGame();
  const [gateOpen, setGateOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (loaded && !parentId) {
      const timer = setTimeout(() => setShowOnboarding(true), 300);
      return () => clearTimeout(timer);
    }
  }, [loaded, parentId]);

  const goToAge = (age) => {
    playSfx("pop", soundOn);
    setTimeout(() => navigate(`/play/${age}`), 150);
  };

  const handlePillarClick = (type) => {
    playSfx("pop", soundOn);
    const cat = type === 'finance' ? 'finance' : 'health';
    navigate(`/play/health?cat=${cat}`);
  };

  return (
    <div
      className="min-h-screen relative overflow-x-hidden grain px-4 pb-10"
      style={{
        background:
          "radial-gradient(circle at 20% 10%, #FEF3C7 0%, transparent 40%), radial-gradient(circle at 80% 90%, #DBEAFE 0%, transparent 40%), linear-gradient(180deg, #FDFBF7 0%, #FEF6E4 100%)",
      }}
    >
      {/* Decorative */}
      <div className="absolute top-12 right-8 w-20 h-20 rounded-full bg-pink-200/40 animate-pulse" />

      {/* Top right toggles */}
      <div className="absolute top-4 right-4 flex gap-2 z-20">
        <button onClick={toggleSound} className="toy-btn w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white shadow-[0_4px_0_rgba(0,0,0,0.15)] flex items-center justify-center">
          {soundOn ? <Volume2 size={18} className="text-sky-500" /> : <VolumeX size={18} className="text-slate-400" />}
        </button>
        <button onClick={toggleLanguage} className="toy-btn h-10 sm:h-12 px-3 rounded-full bg-white shadow-[0_4px_0_rgba(0,0,0,0.15)] flex items-center gap-1 font-display font-bold text-sky-500">
          <Globe size={18} />
          <span className="text-xs sm:text-sm">{language.toUpperCase()}</span>
        </button>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto pt-16 flex flex-col items-center">
        {/* Title */}
        <motion.h1 initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="font-display text-4xl sm:text-6xl font-black text-center tracking-tight mb-2 text-[#1E293B]">
          <span className="inline-block">🍼 </span>
          <span style={{ color: "#38BDF8" }}>{t("app_title", language).split(" ")[0]}</span>{" "}
          <span style={{ color: "#F87171" }}>{t("app_title", language).split(" ")[1]}</span>
        </motion.h1>
        <p className="text-xs sm:text-lg font-bold text-slate-400 mb-10 text-center uppercase tracking-widest">{t("app_tagline", language)}</p>

        {/* AGE SELECTION - Responsive Grid */}
        <h2 className="font-display text-xl sm:text-2xl font-bold mb-6 text-slate-700">👇 {t("pick_age", language)}</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 sm:gap-6 w-full max-w-5xl mb-12">
          {ageList.map((age, idx) => {
            const layer = LAYERS[age];
            return (
              <motion.button
                key={age} onClick={() => goToAge(age)}
                initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 * idx }}
                className="toy-btn relative rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden group border-4 border-white shadow-xl h-40 sm:h-52 flex flex-col"
                style={{ background: layer.color }}
              >
                <div className="p-3 sm:p-4 flex flex-col items-center justify-between h-full w-full text-white">
                    <div className="flex-1 flex items-center justify-center">
                        <div className="rounded-full bg-white p-1 shadow-inner w-16 h-16 sm:w-24 sm:h-24 flex items-center justify-center overflow-hidden">
                            <img src={layer.baby} alt="baby" className="w-full h-full object-cover" />
                        </div>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                        <span className="font-display text-3xl sm:text-5xl font-black drop-shadow-md">{age}</span>
                        <div className="text-left font-black leading-none">
                            <div className="text-[10px] sm:text-xs">{age === 1 ? t("year", language) : t("years", language)}</div>
                            <div className="text-[8px] sm:text-[10px] opacity-80 uppercase">{t(layer.themeKey, language)}</div>
                        </div>
                    </div>
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* REVERTED BUTTONS (PILL STYLE - Optimized for mobile) */}
        <div className="flex flex-col sm:flex-row gap-4 w-full max-w-lg items-stretch sm:items-center justify-center px-4">
            <button
                onClick={() => handlePillarClick('health')}
                className="toy-btn px-6 py-4 sm:py-3 rounded-2xl sm:rounded-full bg-rose-500 shadow-[0_5px_0_#be123c] flex items-center justify-center gap-2 font-display font-black text-white text-sm sm:text-base"
            >
                <Heart size={20} className="fill-current" />
                MISI SEHAT
            </button>

            <button
                onClick={() => setGateOpen(true)}
                className="toy-btn px-6 py-4 sm:py-3 rounded-2xl sm:rounded-full bg-white shadow-[0_5px_0_#CBD5E1] border-2 border-slate-50 flex items-center justify-center gap-2 font-display font-black text-slate-600 text-sm sm:text-base"
            >
                <Settings size={20} />
                {t("parent_corner", language).toUpperCase()}
            </button>

            <button
                onClick={() => handlePillarClick('finance')}
                className="toy-btn px-6 py-4 sm:py-3 rounded-2xl sm:rounded-full bg-blue-600 shadow-[0_5px_0_#1e40af] flex items-center justify-center gap-2 font-display font-black text-white text-sm sm:text-base"
            >
                <Coins size={20} />
                MISI BIJAK
            </button>
        </div>
      </div>

      <ParentGate open={gateOpen} onClose={() => setGateOpen(false)} onPass={() => { setGateOpen(false); navigate("/parent"); }} />
      {showOnboarding && <OnboardingWizard onComplete={() => setShowOnboarding(false)} />}

      {/* Footer Compliance Links for Midtrans Onboarding */}
      <footer className="mt-16 border-t border-slate-200/60 pt-8 pb-4 text-center max-w-4xl mx-auto z-10 relative">
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-4 text-xs font-bold text-slate-500">
          <a href="/terms-conditions" className="hover:text-red-400 transition-colors">Syarat & Ketentuan</a>
          <span>•</span>
          <a href="/privacy" className="hover:text-red-400 transition-colors">Kebijakan Privasi</a>
          <span>•</span>
          <a href="/refund" className="hover:text-red-400 transition-colors">Kebijakan Pengembalian Dana</a>
          <span>•</span>
          <a href="/partner" className="hover:text-red-400 transition-colors">Kemitraan Pengiklan</a>
        </div>
        <p className="text-[11px] text-slate-400 font-bold">
          &copy; {new Date().getFullYear()} Neotunas by BabyLabs. Hak Cipta Dilindungi Undang-Undang.
        </p>
      </footer>
    </div>
  );
}
